import { useCallback, useEffect, useRef, useState } from 'react'
import { HubConnection, HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr'
import { useAuthStore } from '../store/authStore'
import { useLanguageStore } from '../store/languageStore'
import { getApiBaseUrl, delayForTailscaleStagger, getSignalRReconnectDelays, isBackgroundTrafficPaused, isTailscaleBrowserPage } from '../services/apiConfig'
import { secureStorage } from '../utils/secureStorage'
import { generateDeviceId } from '../services/deviceRegistration'
import {
  approveScreenView,
  endScreenViewSession,
  rejectScreenView,
  screenViewErrorMessage,
  type ScreenViewIceServer,
  type ScreenViewPendingRequest,
} from '../services/screenViewService'

type LiveState = {
  sessionGuid: string
  sessionToken: string
  iceServers: ScreenViewIceServer[]
  maxFps: number
  maxWidth: number
  maxHeight: number
}

function hubUrl() {
  return getApiBaseUrl().replace(/\/api\/?$/, '') + '/hubs/screen-view'
}

function toRtcIce(servers: ScreenViewIceServer[] | undefined): RTCIceServer[] {
  return (servers || [{ urls: 'stun:stun.l.google.com:19302' }]).map((s) => ({
    urls: s.urls,
    username: s.username,
    credential: s.credential,
  }))
}

async function captureSmartAccountTab(maxFps: number, maxWidth: number, maxHeight: number) {
  const options = {
    video: {
      frameRate: { ideal: Math.min(maxFps || 10, 15), max: 15 },
      width: { ideal: Math.min(maxWidth || 1920, 1920), max: 1920 },
      height: { ideal: Math.min(maxHeight || 1080, 1080), max: 1080 },
      displaySurface: 'browser',
    },
    audio: false,
    preferCurrentTab: true,
    selfBrowserSurface: 'include',
    surfaceSwitching: 'exclude',
    monitorTypeSurfaces: 'exclude',
  }
  const stream = await navigator.mediaDevices.getDisplayMedia(options as DisplayMediaStreamOptions)
  const track = stream.getVideoTracks()[0]
  const surface = track?.getSettings()?.displaySurface
  if (surface === 'monitor') {
    stream.getTracks().forEach((t) => t.stop())
    throw new Error('DESKTOP_CAPTURE_BLOCKED')
  }
  return stream
}

export function useScreenViewClient() {
  const token = useAuthStore((s) => s.token)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const { language } = useLanguageStore()
  const ar = language === 'ar'

  const [pending, setPending] = useState<ScreenViewPendingRequest | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sharing, setSharing] = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)

  const connectionRef = useRef<HubConnection | null>(null)
  const pcRef = useRef<RTCPeerConnection | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const liveRef = useRef<LiveState | null>(null)
  const pendingRef = useRef<ScreenViewPendingRequest | null>(null)

  const stopMedia = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    pcRef.current?.close()
    pcRef.current = null
    setSharing(false)
  }, [])

  const endLive = useCallback(
    async (reason: string) => {
      const live = liveRef.current
      liveRef.current = null
      stopMedia()
      if (live?.sessionGuid) {
        try {
          await endScreenViewSession(live.sessionGuid, reason)
        } catch {
          /* already closed */
        }
      }
    },
    [stopMedia],
  )

  const startPublishing = useCallback(
    async (live: LiveState, connection: HubConnection) => {
      const stream = await captureSmartAccountTab(live.maxFps, live.maxWidth, live.maxHeight)
      streamRef.current = stream
      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        void endLive('CustomerStopped')
      })

      const pc = new RTCPeerConnection({ iceServers: toRtcIce(live.iceServers) })
      pcRef.current = pc
      stream.getTracks().forEach((track) => pc.addTrack(track, stream))
      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          void connection.invoke('SendIceCandidate', live.sessionGuid, ev.candidate.toJSON())
        }
      }

      await connection.invoke('JoinSession', live.sessionGuid, live.sessionToken)
      const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false })
      await pc.setLocalDescription(offer)
      await connection.invoke('SendOffer', live.sessionGuid, offer)
      setSharing(true)
    },
    [endLive],
  )

  const onApproved = useCallback(
    async (sessionGuid: string, sessionToken: string, iceServers: ScreenViewIceServer[], maxFps: number, maxWidth: number, maxHeight: number) => {
      const connection = connectionRef.current
      if (!connection) return
      const live: LiveState = { sessionGuid, sessionToken, iceServers, maxFps, maxWidth, maxHeight }
      liveRef.current = live
      try {
        await startPublishing(live, connection)
      } catch (err) {
        stopMedia()
        const blocked = err instanceof Error && err.message === 'DESKTOP_CAPTURE_BLOCKED'
        setError(
          blocked
            ? ar
              ? 'لم تبدأ المشاركة لأن النظام يسمح بمشاركة واجهة Smart Account فقط، وليس سطح المكتب كاملاً.'
              : 'Sharing did not start because only the Smart Account window can be shared, not the full desktop.'
            : ar
              ? 'تعذر بدء مشاركة الشاشة. تأكد أنك اخترت تبويب Smart Account.'
              : 'Could not start screen sharing. Choose the Smart Account tab.',
        )
        await endLive('CustomerStopped')
      }
    },
    [ar, endLive, startPublishing, stopMedia],
  )

  useEffect(() => {
    pendingRef.current = pending
    if (!pending) return
    setSecondsLeft(pending.secondsRemaining)
    const id = window.setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setPending(null)
          return 0
        }
        return s - 1
      })
    }, 1000)
    return () => window.clearInterval(id)
  }, [pending])

  useEffect(() => {
    if (!isAuthenticated || !token) return
    let stopped = false
    const deviceId = generateDeviceId()
    const jwt = () => useAuthStore.getState().token || secureStorage.getItem('token') || ''

    const connection = new HubConnectionBuilder()
      .withUrl(hubUrl(), {
        accessTokenFactory: jwt,
        withCredentials: false,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect(getSignalRReconnectDelays())
      .configureLogging(LogLevel.None)
      .build()

    connectionRef.current = connection

    const showPending = (row: ScreenViewPendingRequest) => {
      if (!row?.requestGuid || (row.secondsRemaining != null && row.secondsRemaining <= 0)) return
      setPending(row)
      setError(null)
    }

    connection.on('ScreenViewRequestCreated', (evt: { payload?: ScreenViewPendingRequest }) => {
      if (evt?.payload?.requestGuid) showPending(evt.payload)
    })
    connection.on('ScreenViewRequestRejected', () => setPending(null))
    connection.on('ScreenViewRequestExpired', () => setPending(null))
    connection.on('ScreenViewRequestCancelled', () => setPending(null))
    connection.on('ScreenViewSessionEnded', () => {
      setPending(null)
      stopMedia()
      liveRef.current = null
    })
    connection.on('ScreenViewConnectionLost', () => {
      setPending(null)
      stopMedia()
      liveRef.current = null
    })
    connection.on('requestOffer', async () => {
      const live = liveRef.current
      const pc = pcRef.current
      const conn = connectionRef.current
      if (!live || !pc || !conn) return
      if (pc.localDescription) {
        await conn.invoke('SendOffer', live.sessionGuid, pc.localDescription)
        return
      }
      const offer = await pc.createOffer({ offerToReceiveAudio: false, offerToReceiveVideo: false })
      await pc.setLocalDescription(offer)
      await conn.invoke('SendOffer', live.sessionGuid, offer)
    })
    connection.on('webrtcAnswer', async (msg: { sdp?: RTCSessionDescriptionInit }) => {
      if (msg?.sdp && pcRef.current && !pcRef.current.currentRemoteDescription) {
        await pcRef.current.setRemoteDescription(new RTCSessionDescription(msg.sdp))
      }
    })
    connection.on('webrtcIce', async (msg: { candidate?: RTCIceCandidateInit }) => {
      if (msg?.candidate && pcRef.current) {
        try {
          await pcRef.current.addIceCandidate(msg.candidate)
        } catch {
          /* ignore */
        }
      }
    })

    const heartbeat = window.setInterval(() => {
      const live = liveRef.current
      if (!live || connection.state !== 'Connected') return
      void connection.invoke('Heartbeat', live.sessionGuid).catch(() => undefined)
    }, isTailscaleBrowserPage() ? 20_000 : 8_000)

    void (async () => {
      await delayForTailscaleStagger(isTailscaleBrowserPage() ? 1500 : 0)
      while (!stopped && isBackgroundTrafficPaused()) {
        await new Promise<void>((resolve) => window.setTimeout(resolve, 400))
      }
      if (stopped) return
      try {
        await connection.start()
        await connection.invoke('JoinAsDevice', deviceId)
        // Do not restore a leftover pending overlay on load — that freezes SaaS clients.
      } catch {
        /* hub unavailable; live requests still arrive after reconnect */
      }
    })()

    const onUnload = () => {
      stopMedia()
    }
    window.addEventListener('beforeunload', onUnload)

    return () => {
      stopped = true
      window.clearInterval(heartbeat)
      window.removeEventListener('beforeunload', onUnload)
      stopMedia()
      void connection.stop()
      connectionRef.current = null
    }
  }, [isAuthenticated, token, stopMedia])

  const allow = async () => {
    if (!pending) return
    const row = pending
    setPending(null)
    setBusy(true)
    setError(null)
    try {
      const result = await approveScreenView(row.requestGuid)
      if (result.sessionGuid && result.sessionToken) {
        await onApproved(
          result.sessionGuid,
          result.sessionToken,
          result.iceServers || [],
          result.maxFps,
          result.maxWidth,
          result.maxHeight,
        )
      }
    } catch (err) {
      setError(
        screenViewErrorMessage(
          err,
          ar ? 'تعذر الموافقة على الطلب.' : 'Could not approve the request.',
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  const reject = async () => {
    const row = pending
    setPending(null)
    setBusy(false)
    if (!row) return
    try {
      await rejectScreenView(row.requestGuid)
    } catch {
      /* overlay already closed so the UI is usable again */
    }
  }

  const dismiss = () => {
    setPending(null)
    setBusy(false)
    setError(null)
    stopMedia()
  }

  const stopSharing = async () => {
    await endLive('CustomerStopped')
  }

  return { pending, busy, error, sharing, secondsLeft, ar, allow, reject, dismiss, stopSharing }
}
